import type { LevelDefinition } from "@/levels/types";
import type { BiteKind, Point } from "@/core/types";
import type { RandomSource } from "@/core/rng";
import { createSeededRandom } from "@/core/rng";
import { stepInDirection } from "@/levels/geometry";
import type { DailyChallengeSpecAny, DailyBiteMode } from "./types";
import type { DailyChallengeSpecV3 } from "./v6/types";

/**
 * PR24 (Daily Foundations - version hardening): the one explicit, frozen
 * table of "which historical behavior does Daily generator version N
 * use" - deliberately replaces three previously-inconsistent per-decision
 * checks that used to live here (`speedEngineVersion === SPEED_ENGINE_VERSION_V5`
 * for RUSH debt, a `generatorVersion >= 5` range check for CHAOS rules, and
 * a live-`GameConfig` read with no version fork at all for the persistent
 * HIGH SPEED boost - the last of which meant retuning
 * `GameConfig.speedEngine.highSpeedModifierBoost` later would have silently
 * changed how an already-stored V3/V4/V5 spec replays).
 *
 * Every `generatorVersion` Daily has ever produced (1-5) gets its own
 * explicit entry below, keyed by that exact number - never a `>=`/range
 * check, never a fallback/default case. A future Generator V6 (or any
 * other new version) MUST add its own entry here before a spec of that
 * version can be converted at all (see `dailyVersionRulesFor()` below,
 * which throws for anything unmapped) - there is no way for a new version
 * to silently inherit V5's (or any other version's) behavior.
 */
interface DailyVersionRules {
  rushDebtProfile: "current" | "legacy-v1";
  chaosRulesVersion: "current" | "legacy-v1";
  /**
   * The persistent HIGH SPEED modifier boost this version's spec uses
   * when `modifiers` includes `"high_speed"` - `0` for every version that
   * predates the persistent-modifier-boost concept (V1/V2, "V2 behavior
   * remains frozen"). Deliberately a frozen LITERAL, never a reference to
   * `GameConfig.speedEngine.highSpeedModifierBoost` - that live constant
   * is the tunable for whichever future version reads it next; V3/V4/V5
   * read the frozen copy below instead, forever.
   */
  persistentHighSpeedBoost: number;
}

/**
 * The exact, unchanged value `GameConfig.speedEngine.highSpeedModifierBoost`
 * has held since Generator V3 first shipped it (PR11) through Generator V5
 * (PR13-A) - frozen here, independent of that live constant, so retuning
 * `highSpeedModifierBoost` later (e.g. for a future Daily V6) can never
 * silently change what an already-stored V3/V4/V5 spec's `high_speed`
 * modifier does on replay.
 */
const FROZEN_HIGH_SPEED_BOOST_V3_TO_V5 = 0.08;

const DAILY_VERSION_RULES: Readonly<Record<number, DailyVersionRules>> = {
  1: { rushDebtProfile: "legacy-v1", chaosRulesVersion: "legacy-v1", persistentHighSpeedBoost: 0 },
  2: { rushDebtProfile: "legacy-v1", chaosRulesVersion: "legacy-v1", persistentHighSpeedBoost: 0 },
  3: { rushDebtProfile: "legacy-v1", chaosRulesVersion: "legacy-v1", persistentHighSpeedBoost: FROZEN_HIGH_SPEED_BOOST_V3_TO_V5 },
  4: { rushDebtProfile: "legacy-v1", chaosRulesVersion: "legacy-v1", persistentHighSpeedBoost: FROZEN_HIGH_SPEED_BOOST_V3_TO_V5 },
  5: { rushDebtProfile: "current", chaosRulesVersion: "current", persistentHighSpeedBoost: FROZEN_HIGH_SPEED_BOOST_V3_TO_V5 },
  // V6 (PR25): current RUSH debt/CHAOS tables, same as V5 - V6 never sets
  // the `high_speed` modifier (`modifiers` is always `[]`), so this value
  // is never actually read for a V6 spec, but every version still needs
  // an explicit entry (see this table's own doc).
  6: { rushDebtProfile: "current", chaosRulesVersion: "current", persistentHighSpeedBoost: 0 },
};

/**
 * Looks up `DAILY_VERSION_RULES` for `generatorVersion` - throws for any
 * version without an explicit entry; there is no default/fallback case.
 * A stored spec can only ever carry a `generatorVersion` that
 * `generateDailyChallenge()` was willing to produce, and that function's
 * own dispatch (see `generator.ts`) now throws for anything outside 1-5,
 * so this should never actually throw in production - it exists so
 * shipping a new generator version without also adding a rules entry
 * here fails loudly and immediately, instead of silently reusing V5's
 * (or any other version's) behavior.
 */
function dailyVersionRulesFor(generatorVersion: number): DailyVersionRules {
  const rules = DAILY_VERSION_RULES[generatorVersion];
  if (!rules) {
    throw new Error(
      `daily/convert: no frozen DAILY_VERSION_RULES entry for generatorVersion ${generatorVersion} - add one explicitly before converting/replaying a spec of this version.`,
    );
  }
  return rules;
}

/**
 * Daily's `high_speed` modifier's real, persistent boost for this spec's
 * exact generator version - see `DAILY_VERSION_RULES`'s doc.
 */
function persistentModeBoostFor(spec: DailyChallengeSpecAny): number {
  const rules = dailyVersionRulesFor(spec.generatorVersion);
  return spec.modifiers.includes("high_speed") ? rules.persistentHighSpeedBoost : 0;
}

/**
 * Which `LevelDefinition.rushDebtProfile` this spec's run must use - see
 * `DAILY_VERSION_RULES`'s doc. An already-generated/stored legacy Daily
 * challenge's speed behavior never silently changes just because
 * `GameConfig.speedEngine.rushDebt` is retuned later.
 */
function rushDebtProfileFor(spec: DailyChallengeSpecAny): "current" | "legacy-v1" {
  return dailyVersionRulesFor(spec.generatorVersion).rushDebtProfile;
}

/**
 * Which `LevelDefinition.chaosRulesVersion` this spec's run must use - see
 * `DAILY_VERSION_RULES`'s doc. An already-generated/stored legacy Daily
 * challenge's CHAOS sequence (SCORE HIT reachability included) never
 * silently reinterprets just because
 * `GameConfig.bites.chaos.outcomeWeightsCurrentV2` is retuned later.
 */
function chaosRulesVersionFor(spec: DailyChallengeSpecAny): "current" | "legacy-v1" {
  return dailyVersionRulesFor(spec.generatorVersion).chaosRulesVersion;
}

/**
 * Section 8 (PR25) - the exact 4 forward cells V6's mandatory safe
 * opening guarantees are legal, recomputed here (never stored on the
 * spec itself - deterministic from `snakeStart`/`startDirection` alone)
 * so `LevelGame`'s initial fruit spawn can exclude them via
 * `LevelDefinition.initialFruitExclusionCells`. Mirrors
 * `checkForwardOpeningSafety()`'s own cell order (closest first) -
 * `generatorV6.ts`'s acceptance pipeline already guarantees these 4
 * cells are legal/obstacle-free before a spec is ever accepted, so this
 * never needs to re-validate them, only recompute their coordinates.
 */
function v6SafeOpeningCells(spec: DailyChallengeSpecV3): Point[] {
  const cells: Point[] = [];
  let cursor = spec.snakeStart;
  for (let i = 0; i < 4; i++) {
    cursor = stepInDirection(cursor, spec.startDirection);
    cells.push(cursor);
  }
  return cells;
}

/**
 * Converts a `DailyChallengeSpecAny` (V1 or V2 - see `types.ts`'s doc)
 * into the exact `LevelDefinition` shape `LevelGame`/`LevelValidator`/
 * `GameScene`'s rendering already know how to run - the whole point of
 * the framework-agnostic spec: nothing downstream needs to know a level
 * came from Daily Challenge generation rather than a hand-authored
 * `worldN.ts` file. `id` is namespaced (`daily-<challenge_id>`) so it can
 * never collide with a real campaign level id, and is never looked up
 * through `LevelRegistry` (Daily Challenge levels are never registered
 * there - see the PR description's "Campaign and Daily state must remain
 * completely separate" note). `pace` is deliberately omitted - Daily has
 * its own Daily Points formula instead (see `dailyPoints.ts`).
 *
 * V1 vs V2 objective: a `formatVersion: 1` spec (an already-stored row
 * from before this Generator V2/Survival redesign - see the README's "V1
 * stored rows" section) converts to `objective: "campaign"` - it plays
 * out using the exact original target-length/gate rule it was generated
 * under, since that's the only rule its `targetLength` was ever chosen
 * to be achievable against. A `formatVersion: 2` spec converts to
 * `objective: "survival"` with its own `pressureProfile` - never the
 * other way around, and a V1 spec's `objective` is never "survival" even
 * though the type happens to have a `targetLength` field either way -
 * "never pretend a V1 spec contains V2 scoring/pressure data".
 */
export function dailyChallengeToLevelDefinition(spec: DailyChallengeSpecAny): LevelDefinition {
  const base: LevelDefinition = {
    id: `daily-${spec.challengeId}`,
    name: "DAILY CHALLENGE",
    worldId: spec.themeWorldId,
    levelInWorld: 1,
    order: 0,
    grid: spec.grid,
    snakeStart: spec.snakeStart,
    startDirection: spec.startDirection,
    initialLength: spec.startingLength,
    targetLength: spec.formatVersion === 1 ? spec.targetLength : spec.startingLength,
    baseStepMs: spec.baseStepMs,
    obstacles: spec.obstacles,
    exit: spec.exit,
    // PR11 (Speed Engine V2): every Daily attempt (any generator version,
    // even a legacy V1 date) uses Daily's own, tighter speed caps -
    // deliberately independent of `objective` below, which a V1 spec
    // still sets to `"campaign"` for its gate/target-length rule. PR25
    // (Daily V6): a `formatVersion: 3` spec uses its OWN, lower caps
    // (`DAILY_V6_SPEED_CAPS`, 75ms normal floor vs. V1-V5's 85ms) - see
    // `SpeedEngine.ts`'s doc for why V6 needs a distinct `speedMode`
    // rather than reusing `"daily"`.
    speedMode: spec.formatVersion === 3 ? "daily-v6" : "daily",
    persistentModeBoost: persistentModeBoostFor(spec),
    rushDebtProfile: rushDebtProfileFor(spec),
    chaosRulesVersion: chaosRulesVersionFor(spec),
  };

  if (spec.formatVersion === 1) {
    return { ...base, objective: "campaign", targetLength: spec.targetLength };
  }
  if (spec.formatVersion === 3) {
    return {
      ...base,
      objective: "survival",
      pressureCurve: spec.pressureCurve,
      portals: spec.portals,
      blackHoles: spec.blackHoles,
      initialFruitExclusionCells: v6SafeOpeningCells(spec),
    };
  }
  return { ...base, objective: "survival", pressure: spec.pressureProfile };
}

const BITE_MODE_ALLOWED_KINDS: Record<DailyBiteMode, readonly BiteKind[]> = {
  standard: ["safe", "rush", "chaos"],
  safe_only: ["safe"],
  rush_only: ["rush"],
  chaos_only: ["chaos"],
};

export function allowedBiteKindsForMode(mode: DailyBiteMode): readonly BiteKind[] {
  return BITE_MODE_ALLOWED_KINDS[mode];
}

/**
 * The exact `LevelGame` constructor options a Daily Challenge attempt
 * must use: bite placement and CHAOS both driven by this challenge's own
 * stored `rngSeeds` (never `Math.random`) - every attempt at the same
 * challenge, by any player, sees the identical fruit-placement/CHAOS
 * sequence, so the challenge is genuinely learnable across the 3
 * attempts. `allowedBiteKinds` restricts fruit spawns to `spec.biteMode`.
 * STARFRUIT ("GIGA") is deliberately disabled outright for Daily
 * (`starfruitRandom` always resolves to "never eligible") - it has no
 * gate for STARFRUIT to open in survival mode anyway, and V1's rule was
 * the same; unaffected in Campaign, which never passes this option at all.
 */
export function dailyLevelGameOptions(spec: DailyChallengeSpecAny): {
  random: RandomSource;
  chaosRandom: RandomSource;
  starfruitRandom: RandomSource;
  allowedBiteKinds: readonly BiteKind[];
} {
  return {
    random: createSeededRandom(spec.rngSeeds.board),
    chaosRandom: createSeededRandom(spec.rngSeeds.chaos),
    starfruitRandom: () => 1,
    allowedBiteKinds: allowedBiteKindsForMode(spec.biteMode),
  };
}
